/**
 * The deterministic envelope + the typed error — the TS mirror of `api/schemas/common.py`'s
 * `Envelope` / `ResponseMeta` / `ApiErrorBody` (mcp-api.md §1.6, §2.1).
 *
 * EVERY tool route — over MCP and over REST — returns `Envelope<T>` = `{ok, data, error,
 * meta}`. The frontend-supporting endpoints (`health/*`, `system/*`, ...) are the one
 * exception: they return their payload *bare*, not enveloped (see `rest_app.py` — those
 * handlers are typed `-> ExtractionQueueStatus` etc., not `-> Envelope[...]`). The REST
 * client therefore has two unwrap paths; see `src/api/client.ts`.
 */

import type { ErrorCategory } from './enums';

/**
 * The serialized error shape carried in `Envelope.error` — mirrors `ApiErrorBody`.
 *
 * Note: the backend's wire `ApiErrorBody` does NOT carry a `category` field — the
 * category is implied by the stable `code`. The frontend's `ErrorEnvelope` (the shape
 * `client.ts` normalizes *every* failure into, per frontend.md §3.1) DOES carry an
 * explicit `category` so the three UI handling paths (frontend.md §4.2) can branch on it.
 * `client.ts` derives `category` from `code` via `STABLE_CODE_CATEGORY`.
 */
export interface ApiErrorBody {
  /** Stable machine code — see api/errors.py's stable-code table. */
  code: string;
  /** Human-readable, safe to surface in the UI. */
  message: string;
  /** Whether the handling contract permits an automatic retry. */
  retryable: boolean;
  /** Structured context — ids, counts, the offending field. Never secrets. */
  details: Record<string, unknown> | null;
}

/** The per-call metadata block every `Envelope` carries — mirrors `ResponseMeta`. */
export interface ResponseMeta {
  /** Correlation id; echoes into every log line for the request. */
  queryId: string;
  elapsedMs: number;
  /** True iff a hard cap clipped the result — paginate. */
  truncated: boolean;
  /** Best-effort total match count; null when not cheaply knowable. */
  totalEstimate: number | null;
  /** Opaque cursor for the next page; null when the listing is exhausted. */
  nextCursor: string | null;
  /** The active capability tier (D-004). */
  capabilityTier: string;
  /** Non-fatal notes, e.g. 'rerank skipped: lean tier'. */
  warnings: string[];
}

/**
 * The uniform `{ok, data, error, meta}` envelope every tool returns — mirrors `Envelope<T>`.
 * Exactly one of `data` / `error` is populated; `meta` is always present.
 */
export interface Envelope<T> {
  ok: boolean;
  data: T | null;
  error: ApiErrorBody | null;
  meta: ResponseMeta;
}

/**
 * The normalized error the frontend works with everywhere — frontend.md §3.1.
 *
 * `client.ts` builds this from a failed `Envelope`, a bare-endpoint error body, an HTTP
 * status, or a thrown network/parse error — so every `@tanstack/query` `onError`,
 * `<ErrorBoundary>`, and `<ToastHost>` sees one shape with an explicit `category`.
 */
export interface ErrorEnvelope {
  category: ErrorCategory;
  code: string;
  message: string;
  retryable: boolean;
  context: Record<string, unknown> | undefined;
  /** The correlation id, when one was minted/echoed — the user can quote it. */
  queryId: string | undefined;
}

/**
 * The thrown error class. `@tanstack/query` catches it; the error-handling layer
 * (`hooks/useErrorHandler`, `<ErrorBoundary>`) reads `.envelope` off it.
 */
export class AletheiaApiError extends Error {
  public readonly envelope: ErrorEnvelope;

  constructor(envelope: ErrorEnvelope) {
    super(envelope.message);
    this.name = 'AletheiaApiError';
    this.envelope = envelope;
  }
}
